// Lo que nunca debe viajar a Business Central en la cabecera de un documento.
//
// La cabecera se arma con `{...invoice}`, y `invoice` es **la fila entera de la
// base local**: lleva el identificador de Dexie, las banderas de sincronización,
// lo que contestó Business Central la vez anterior y lo que dejó puesto el
// formulario. Todo eso se le mandaba de vuelta en cada insert. Se parcheaba con
// seis `delete` sueltos, uno por cada campo que alguien vio hacer daño.
//
// Un campo de más no falla: el importador de Business Central ignora lo que no
// conoce, y por eso ninguno de los seis se descubrió hasta que rompió algo --el
// turno que se guardaba en 0 por mandar `turno` y `Turno` a la vez, el tipo de
// comprobante vacío que rechaza Hacienda--. Los siguientes se descubrirían igual.
//
// **Medido contra Business Central el 18/09/2026**, mandando cabeceras gemelas
// --una con los campos en discusión y otra limpia-- por la misma acción que usa
// el terminal, en CRI DEV y en PAN DEV. Los documentos que salieron difieren
// **sólo en `Id_TPV`, `No` y `SystemId`**: en todo lo demás son idénticos. Las
// tres preguntas que quedaban abiertas se contestaron así:
//
//  1. Los campos que arrastraba el spread **se ignoran**: ninguno de los siete
//     centinelas llegó a guardarse, y la basura local tampoco rompió nada.
//  2. **La idempotencia la sostiene `Id_TPV`, no el número.** Repetir el mismo
//     `Id_TPV` con un `No` inventado y un `systemId` en ceros devolvió el
//     documento que ya existía, sin duplicar. `No` y `systemId` son ruido.
//  3. Los campos fiscales de Panamá --`CUFE` y compañía-- **se ignoran** en el
//     insert, así que reintentar un documento ya autorizado no los reescribe.
//
// De ahí lo que se quita ahora, y lo que **no**:
//
// - **`Pre_Assigned_No` se queda.** No es un campo de respuesta: es el número
//   que pone el terminal, y es por donde vuelve a encontrar su documento
//   registrado --`getFinalDocumentNo` filtra la entidad de registradas por él--.
//   No se publica en el borrador, así que la medición no puede verlo; quitarlo
//   dejaría la venta sin número y sin forma de recuperarlo.
// - **`NCR`, `RTN`, `No_Autorizacion_CAI` y los `Student_*` se quedan.** Se
//   midieron ignorados en Costa Rica y Panamá, pero son los campos fiscales de
//   Honduras y de Perú, y ahí no se pudo medir: ECU TEST no admite ni una
//   factura --las 16 cajas tienen la serie sin número de autorización o sin
//   serie-- y de Honduras no hay ambiente. Ignorados en dos países no es
//   ignorados en los cuatro.

/**
 * Campos de control de la base local. No existen en Business Central.
 */
const DE_LA_BASE_LOCAL = [
  "id",
  "clientLocalId",
  "pendingSync",
  "lastModified",
  "lastSynced",
  "deleted",
  "syncing",
  "syncStartTime",
  "syncStage",
  "syncError",
  "syncErrorMessage",
  "posted",
  "isDraft",
  "draftNo",
  "fullyReturned",
  "partiallyReturned",
];

/**
 * Marcas que pone el terminal para decidir qué documentos se pueden abonar. Se
 * calculan al leer de Business Central; devolvérselas no aporta nada.
 */
const MARCAS_DE_EXCLUSION = [
  "DocumentoBorradoTPV",
  "Cancelled",
  "Corrective",
  "Anulado_TPV",
  "Anulado_por_Documento",
  "Anulados",
  "Oculto",
];

/**
 * Lo que dejó puesto el formulario y no es un campo del documento.
 *
 * `Customer_No` no está acá a propósito: aunque el formulario lo ponga, el
 * número de cliente sí se manda, por `Sell_to_Customer_No_` y
 * `Bill_to_Customer_No_`, que se asignan explícitamente.
 */
const DEL_FORMULARIO = [
  "couponDetail",
  "Customer_Type",
  "Vendor_Type",
  "Customer_Price_Group",
  "Customer_Disc_Group",
];

/**
 * Las dos variantes en minúscula.
 *
 * Mandar `caja` y `Caja` a la vez hacía que el importador guardara el valor mal
 * --el turno quedaba en 0--. Ya se borraban a mano; siguen acá para que el
 * motivo esté escrito con los demás.
 */
const EN_MINUSCULA = ["caja", "turno"];

/**
 * Lo que contestó Business Central y se le devolvía en el insert siguiente.
 *
 * Medido: los ignora. El número lo asigna él --una cabecera sin `No` sale con el
 * suyo-- y para reconocer un documento repetido usa `Id_TPV`, no el número: la
 * misma `Id_TPV` con un `No` inventado devolvió el documento que ya existía.
 *
 * `Pre_Assigned_No` **no está acá**: ése lo pone el terminal y es por donde
 * reencuentra su documento registrado.
 */
const RESPUESTA_DE_BC = ["No", "systemId"];

/**
 * Los campos fiscales que Panamá copia del documento autorizado.
 *
 * Medido en PAN DEV: el insert los ignora. Se quitan igual, porque reenviar el
 * CUFE de un documento en el alta de otro no significa nada y es de las cosas
 * que un día alguien decide leer.
 */
const FISCALES_DE_PANAMA = [
  "CUFE",
  "Numero_Fiscal",
  "Tipo_Documento_DGI",
  "Tipo_Emision_DGI",
  "Numero_Fiscal_Referencia",
];

export const CAMPOS_QUE_NO_VIAJAN = [
  ...DE_LA_BASE_LOCAL,
  ...MARCAS_DE_EXCLUSION,
  ...DEL_FORMULARIO,
  ...EN_MINUSCULA,
  ...RESPUESTA_DE_BC,
  ...FISCALES_DE_PANAMA,
];

/**
 * Quita de la cabecera lo que no tiene sentido en Business Central.
 *
 * Modifica el objeto que recibe, que es como estaban escritos los `delete` que
 * sustituye.
 *
 * @param {object} cabecera
 * @returns {object} La misma cabecera, para poder encadenar.
 */
export const quitarLosCamposLocales = (cabecera) => {
  if (!cabecera) return cabecera;
  for (const campo of CAMPOS_QUE_NO_VIAJAN) delete cabecera[campo];
  return cabecera;
};
